import { CallButton } from '@/components/CallButton';
import { Icon } from '@/components/Icon';
import { UrlForm } from '@/components/UrlForm';
import { WAVE_COMMENTS, WAVE_REACTIONS } from '@/content/product';
import { FOUNDER_PHOTO_SIZE, FOUNDERS } from '@/content/social-proof';
import { useCountUp } from '@/lib/useCountUp';
import { useLang, useT } from '@/lib/useLang';

/**
 * Une vague en cours, pas une vague finie : l'aperçu montre ce que le client
 * voit quelques heures après avoir publié, avec la jauge encore en train de
 * monter. Les cibles restent celles de `content/product.ts`.
 */
const PREVIEW_REACTIONS = Math.round(WAVE_REACTIONS * 0.8);
const PREVIEW_COMMENTS = Math.max(1, WAVE_COMMENTS - 1);

/**
 * Le hero d'une agence, sur le modèle de linkforce.in : panneau sombre, titre
 * à la première personne du pluriel, et les visages de ceux qui font le
 * travail avant toute capture de produit. Le premier coup d'œil doit dire
 * « des gens s'en occupent », pas « un outil de plus ».
 *
 * Le paiement reste l'action principale et reste ici : le champ LinkedIn mène
 * droit au checkout, sans détour. L'appel est le second bouton, masqué tant
 * qu'il n'y a pas de page de réservation (`BOOK_CALL_URL`, `/call`).
 */
export function Hero() {
  const t = useT();

  return (
    <section id="top" className="nz-hero">
      <div className="nz-hero__panel">
        <div className="nz-hero__copy">
          <div className="nz-pill nz-pill--dark">
            <Icon name="users" className="nz-pill__icon" />
            {t.heroBadge}
          </div>
          <h1 className="nz-hero__h1">
            {t.heroH1a} <span>{t.heroH1b}</span>
          </h1>
          <p className="nz-hero__p1">{t.heroP1}</p>

          <UrlForm id="hero-linkedin-url" />

          <div className="nz-hero__actions">
            <CallButton placement="hero" tone="dark" />
          </div>

          <ul className="nz-hero__checks">
            {[t.guarantee, t.noAccess, t.noCommitment].map((item) => (
              <li key={item}>
                <Icon name="check" className="nz-hero__check" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <HeroMosaic />
      </div>
    </section>
  );
}

/**
 * Les deux fondateurs, en vrai, et une vague en cours par-dessus. Le trait
 * bleu griffonné reprend celui de Linkforce, aux couleurs de NeverZero.
 */
function HeroMosaic() {
  const lang = useLang();

  return (
    <div className="nz-mosaic">
      <svg className="nz-mosaic__scribble" viewBox="0 0 220 120" aria-hidden="true" focusable="false">
        <path d="M8 92C40 30 92 14 120 40s-6 62-30 48 10-66 58-70 64 22 64 22" />
      </svg>
      <div className="nz-mosaic__photos">
        {FOUNDERS.map((founder, index) => (
          <figure key={founder.name} className={`nz-mosaic__photo nz-mosaic__photo--${index + 1}`}>
            <picture>
              <source srcSet={founder.photo.webp} type="image/webp" />
              <img
                src={founder.photo.fallback}
                alt={`${founder.name}, ${founder.role[lang]}`}
                width={FOUNDER_PHOTO_SIZE.width}
                height={FOUNDER_PHOTO_SIZE.height}
              />
            </picture>
            <figcaption>
              <b>{founder.firstName}</b>
              <span>{founder.role[lang]}</span>
            </figcaption>
          </figure>
        ))}
      </div>
      <HeroPreview />
    </div>
  );
}

function HeroPreview() {
  const t = useT();
  const reactions = useCountUp(PREVIEW_REACTIONS);

  return (
    <div className="nz-teamcard nz-mosaic__card">
      <div className="nz-toast">
        <span className="nz-toast__icon">
          <Icon name="rocket" className="nz-toast__glyph" />
        </span>
        <span>
          <b>{t.previewToast}</b>
          <small>{t.previewToastTime}</small>
        </span>
      </div>

      <div className="nz-progress">
        <div className="nz-progress__title">{t.previewProgress}</div>
        <ProgressRow label={t.previewReactions} value={reactions} target={WAVE_REACTIONS} />
        <ProgressRow label={t.previewComments} value={PREVIEW_COMMENTS} target={WAVE_COMMENTS} />
        <p className="nz-progress__note">{t.previewNote}</p>
      </div>
    </div>
  );
}

function ProgressRow({ label, value, target }: { label: string; value: number; target: number }) {
  return (
    <div className="nz-progress__row">
      <div className="nz-progress__labels">
        <span>{label}</span>
        <b>
          {value}/{target}
        </b>
      </div>
      <div className="nz-progress__track">
        <div className="nz-progress__bar" style={{ width: `${Math.round((value / target) * 100)}%` }} />
      </div>
    </div>
  );
}
